import {Box, Center, Hide, HStack, Icon, Show, Spinner, ThemeIconKey} from 'platform/foundation';
import styled, {DefaultTheme} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {always, isNil} from 'ramda';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

type ButtonSize = 'default' | 'small' | 'xSmall';
type ButtonVariant = 'black' | 'orange' | 'outline';

export interface ButtonProps extends RequiredTestIdProps {
  title?: string;
  size?: ButtonSize;
  variant: ButtonVariant;
  isDisabled?: boolean;
  isLoading?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  leftIcon?: ThemeIconKey;
  rightIcon?: ThemeIconKey;
}

export function Button(props: ButtonProps) {
  const isDisabled = Boolean(props.isDisabled || props.isLoading);

  return (
    <StyledButton
      role="button"
      $variant={props.variant}
      $size={props.size ?? 'default'}
      $isDisabled={isDisabled}
      $iconOnly={isNil(props.title)}
      data-testid={suffixTestId('buttonIcon', props)}
      onClick={props.onClick}
    >
      <HStack justify="center" spacing={2} align="center">
        <Hide when={isNil(props.leftIcon)}>
          <Icon value={props.leftIcon} size={5} />
        </Hide>
        <Hide when={isNil(props.title)}>
          <Center minHeight={5}>{props.title}</Center>
        </Hide>
        <Hide when={isNil(props.rightIcon)}>
          <Icon value={props.rightIcon} size={5} />
        </Hide>
      </HStack>
      <Show when={props.isLoading}>
        <Box position="absolute" top={0} right={0} bottom={0} left={0}>
          <HStack align="center" justify="center" height="100%">
            <Spinner color={props.variant === 'black' ? 'dark' : 'light'} />
          </HStack>
        </Box>
      </Show>
    </StyledButton>
  );
}

const getHorizontalPadding = (size: ButtonSize, iconOnly: boolean, theme: DefaultTheme) =>
  match<[ButtonSize, boolean]>([size, iconOnly])
    .with(['default', Pattern.any], always(theme.getSize(6)))
    .with(['small', false], always(theme.getSize(4)))
    .with(['small', true], always(theme.getSize(2)))
    .with(['xSmall', Pattern.any], always(theme.getSize(2)))
    .exhaustive();

const getVerticalPadding = (size: ButtonSize, theme: DefaultTheme) =>
  match([size])
    .with(['default'], always(theme.getSize(3)))
    .with(['small'], always(theme.getSize(2)))
    .with(['xSmall'], always('2px'))
    .exhaustive();

// eslint-disable-next-line eag/no-css-property
const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $isDisabled: boolean;
  $size: ButtonSize;
  $iconOnly?: boolean;
}>`
  outline: none;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  position: relative;
  padding: ${({theme, $size, $iconOnly}) =>
    `${getVerticalPadding($size, theme)} ${getHorizontalPadding($size, !!$iconOnly, theme)}`};
  border-radius: ${({theme, $size}) =>
    $size === 'xSmall' ? theme.radii.xSmall : theme.radii.small};
  line-height: ${({theme}) => theme.lineHeights.text.small};
  opacity: ${({$isDisabled}) => ($isDisabled ? 0.5 : 1)};
  cursor: ${({$isDisabled}) => ($isDisabled ? 'auto' : 'pointer')};
  pointer-events: ${({$isDisabled}) => ($isDisabled ? 'none' : 'all')};
  font-family: ${({theme}) => theme.fonts.body};
  font-size: ${({theme}) => theme.fontSizes.text.small};
  font-weight: ${({theme}) => theme.fontWeights.text.default};
  letter-spacing: 0;
  color: ${({$variant, theme}) =>
    match<ButtonVariant>($variant)
      .with('black', always(theme.colors.text.white))
      .with('orange', always(theme.colors.text.primary))
      .with('outline', always(theme.colors.text.primary))
      .exhaustive()};
  background-color: ${({$variant, theme}) =>
    match<ButtonVariant>($variant)
      .with('black', always(theme.colors.accent.complementary))
      .with('orange', always(theme.colors.accent.primary))
      .with('outline', always(theme.colors.general.transparent))
      .exhaustive()};
  border: ${({theme}) => `1px solid ${theme.colors.border.emphasisDefault}`};

  &:hover {
    color: ${({$variant, theme}) =>
      match<ButtonVariant>($variant)
        .with('black', always(theme.colors.text.white))
        .with('orange', always(theme.colors.text.primary))
        .with('outline', always(theme.colors.text.white))
        .exhaustive()};
    background-color: ${({$variant, theme}) =>
      match<ButtonVariant>($variant)
        .with('black', always(theme.colors.accent.darkGrey))
        .with('orange', always(theme.colors.accent.secondary))
        .with('outline', always(theme.colors.accent.complementary))
        .exhaustive()};
  }
`;
